import type { ProjectDocument } from "../types/documents";

type ApiUploadResponse =
  | {
      ok: true;
      document: ProjectDocument;
    }
  | {
      ok: false;
      error?: string;
      reason?: string;
    };

export async function uploadProjectDocument(
  projectId: string,
  specSourceId: string | null,
  file: File,
): Promise<ProjectDocument> {
  if (!projectId) {
    throw new Error("project id is required");
  }

  if (!file) {
    throw new Error("file is required");
  }

  const formData = new FormData();
  formData.append("file", file);
  formData.append("projectId", projectId);

  if (specSourceId) {
    formData.append("specSourceId", specSourceId);
    formData.append("sourceId", specSourceId);
  }

  const searchParams = new URLSearchParams({ projectId });

  if (specSourceId) {
    searchParams.set("specSourceId", specSourceId);
  }

  const response = await fetch(`/api/documents?${searchParams.toString()}`, {
    method: "POST",
    body: formData,
  });

  const payload = (await response.json()) as ApiUploadResponse;

  if (!payload || payload.ok !== true) {
    const message = payload?.error ?? payload?.reason ?? `Failed to upload document (status ${response.status})`;
    throw new Error(message);
  }

  return payload.document;
}

export async function uploadProjectDocumentToProject(
  projectId: string,
  file: File,
  options: { title?: string; context?: string } = {},
): Promise<ProjectDocument> {
  if (!projectId) {
    throw new Error("project id is required");
  }

  if (!file) {
    throw new Error("file is required");
  }

  const formData = new FormData();
  const title = options.title?.trim() || file.name;

  formData.append("title", title);
  formData.append("file", file);

  if (options.context?.trim()) {
    formData.append("context", options.context.trim());
  }

  const response = await fetch(`/api/projects/${projectId}/documents`, {
    method: "POST",
    body: formData,
  });

  const payload = await safeJson<ApiUploadResponse>(response);

  if (!payload || payload.ok !== true) {
    const message = payload?.error ?? payload?.reason ?? `Failed to upload document (status ${response.status})`;
    throw new Error(message);
  }

  return payload.document;
}

async function safeJson<T>(response: Response): Promise<T | null> {
  try {
    return (await response.json()) as T;
  } catch (error) {
    console.warn("Failed to parse JSON response", error);
    return null;
  }
}
